import KeyMomentSettings from "../custom-components/AggregationComponents/KeyMomentSettings";
import Box from "@mui/material/Box";
import SimpleComposer from "../custom-components/AggregationComponents/SimpleComposer";
import Button from "@mui/material/Button";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    CLIP_PAGE_NAME,
    CURRENT_SPORT,
    DEFAULT_CLIP_NAMES,
    defaultContentSpecificParams,
    EDITOR_PAGE_NAME, SELECTION_LIMIT_MINUTES,
    SUPPORTED_CONTENTS,
    SUPPORTED_SPORTS
} from "../../constants/globalConstants";
import Card from "@mui/material/Card";
import {getKeyMoments, suggest} from "../../queries/keyMomentQueries";
import MaterializationSettings from "../../custom-components/Editor/MaterializationSettings/MaterializationSettings";
import SlowMotionVideoIcon from '@mui/icons-material/SlowMotionVideo';
import EditIcon from '@mui/icons-material/Edit';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ShareIcon from '@mui/icons-material/Share';
import {getClipsKeyMomentByIds, getLogicKeyMomentsAsCards, materializeLogicClip} from "../../queries/clipQueries";
import {CollectionClip} from "../../types/collectionTypes";
import Stack from "@mui/material/Stack";
import WarningBanner from "../../pages/custom-components/CommonComponents/WarningBanner";
import LabelValueBox from "../../pages/custom-components/CommonComponents/LabelValueBox";
import {formatTime, formatUTCDate, getContentRenderParams, getSubRoot} from "../../utils/utils";
import {useSelectedCard} from "../../contexts/SelectedCardContext";
import {useNavigate} from "react-router";
import {profMark, profMarkAfterPaint} from "@/utils/renderProfiler";
import {DASHED_SEPARATOR_STYLE, OP_BUTTON_STYLE, PANEL_BUTTON_HEIGHT} from "../../constants/styles";
import {ButtonWithPopover, ButtonWithPopoverRef} from "../custom-components/CommonComponents/ButtonWithPopover";
import {enqueueSnackbar} from "notistack";
import {useLocation} from "react-router-dom";
import {usePageSettings} from "@/contexts/PageSettingsContext";
import {EDITOR_CONTENT_TYPE, ModeStatus} from "@/types/editorTypes";
import ConfirmTextField from "@/pages/custom-components/CommonComponents/ConfirmTextField";
import {IconButton, LinearProgress, Tooltip, Typography} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import { circularIconHoverSx } from "@/styles/iconHover";
import MatchHighlightHeader
    from "@/pages/custom-components/AggregationComponents/AggregationHeaders/MatchHighlightHeader";
import {FilterOption} from "@/types/filterTypes";
import {GoalParadeParams, MatchHighlightParams} from "../../types/contentTypes";
import {useContentSpecificParams} from "@/contexts/ContentSpecificParamsContext";
import {PANEL_MODE} from "@/types/generic";
import {
    ASPECT_RATIO_OPTIONS
} from "@/reactvideoeditor/pro/components/advanced-timeline/components/timeline-header/aspect-ratio-dropdown";
import {extractPlatCuts, findClosestAspectRatio} from "@/utils/generic";
import {useEditorKeyMoments} from "@/contexts/EditorKeyMomentsCuts";
import {useSelectedContent} from "@/contexts/SelectedContentContext";
import {useEditorialAssets} from "../../contexts/EditorialAssetsContext";
import EditorialAssetList from "@/pages/custom-components/EditorAssetComponents/EditorialAssetList";
import {EditorialAsset} from "@/types/editorialAssetsTypes";
import {useMaterializationSettings} from "@/contexts/MaterializationSettingsContext";
import {defaultFadeDuration} from "@/utils/jobUtils";
import {TRIGGER_ACTION} from "@/types/ruleTypes";
import DividerLabel from "@/pages/custom-components/CommonComponents/DividerLabel";
import Grid from "@mui/material/Grid";
import {HIGHLIGHT_OPTION_SHEMA, KeyMomentCut} from "@/types/keyMomentTypes";
import {applyKeyMomentFilter} from "@/utils/filterUtils";
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import {useSSEProvider} from "@/contexts/application/SSEProvider";
import StopIcon from "@mui/icons-material/Stop";
import {stopJob} from "@/queries/jobQueries";
import GoalParadeHeader from "@/pages/custom-components/AggregationComponents/AggregationHeaders/GoalParadeHeader";
import {useEditState} from "@/contexts/editor/EditStateContext";
import {EDITORIAL_ASSETS, INTERLEAVE_MODE, OverlayRenderParametersResponse} from "types/editorialAssetsTypes";
import {getOverlayUniqueId} from "utils/editor/editorUtils";
import {useNavigationHistory} from "@/contexts/NavigationContext";
import OptionButtonGroup from "@/pages/custom-components/CommonComponents/OptionButtonGroup";
import {getAssetDefaultRenderParameters} from "queries/editorialAssetQueries";
import {OverlayType} from "reactvideoeditor/pro/types";
import {SSEJobs} from "@/pages/custom-components/utils/sseUtils";
import {usePageJump} from "../../hooks/usePageJump";

import CustomSortingSelector from "pages/custom-components/CustomSortingSelector";
import {getDistinctEvents, sortObjects} from "utils/filterUtils";
import {useClipOperations} from "@/hooks/useClipOperations";
import {getCardSelectionDuration} from "@/utils/types/cardUtils";
import {LoadingButton} from "@mui/lab";
import {createOrphanCutsCopy} from "@/queries/keyMomentCutQueries";
import {ClipOverlay} from "@/reactvideoeditor/pro/types";
import {mapKeyMomentToOverlays} from "@/utils/editor/timelineMappingUtils";
import {FPS, MAIN_TRACK_ID} from "@/constants";
import {useIntl} from "react-intl";
import {addEditorialAsset, moveReplaySegmentsToReplayTrack} from "@/utils/editor/editorUtils";

interface ViewToggleProps {
    gridView: boolean;
    setGridView: (next: boolean) => void;
}

interface AudioTrackInfos{
    severity: "success" | "warning" | "error"
    message: string
    title: string
}

const ViewToggle = ({ gridView, setGridView }: ViewToggleProps) => {
  const toggle = () => {
    const next = !gridView;
    setGridView(next);
  };

  return (
    <IconButton
      onClick={toggle}
      sx={{
        ...circularIconHoverSx(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ml: 1.5,
      }}
    >
      {gridView ? <ViewListIcon /> : <GridViewIcon />}
    </IconButton>
  );
}

const CARD_NUMBER_TO_ENABLE_GRID = 4

function mapSelection(keyMoments: CollectionClip[]): CollectionClip[]{
    return keyMoments.map((el :any) => {return {...el, selected:true, suggested:true}})
}

export enum AGGREGATION_USAGE_TYPE {
    STANDARD = 'standard',
    PRESET = 'preset',
}

interface ContentAggregationProps {
    usageMode?: AGGREGATION_USAGE_TYPE
    onActionChange?: (action: string) => void
    actions?: string[]
    action?: string
}

const getActionIcon = (action: string) => {
    switch (action) {
        case TRIGGER_ACTION.CREATE_LOGIC_CLIP: return <SaveIcon />;
        case TRIGGER_ACTION.CREATE_MATERIALIZE_LOGIC_CLIP: return <FileCopyIcon/>;
        case TRIGGER_ACTION.CREATE_MATERIALIZE_PUBLISH_LOGIC_CLIP: return <ShareIcon/>;
        default: return <></>;
    }
}

const getActionLabel = (action: string) => {
    switch (action) {
        case TRIGGER_ACTION.CREATE_LOGIC_CLIP: return "Save";
        case TRIGGER_ACTION.CREATE_MATERIALIZE_LOGIC_CLIP: return "Render";
        case TRIGGER_ACTION.CREATE_MATERIALIZE_PUBLISH_LOGIC_CLIP: return 'Publish';
        case TRIGGER_ACTION.NOOP: return 'NOOP'
        default: return action;
    }
}

const ContentAggregation: React.FC<ContentAggregationProps> = ({usageMode, onActionChange = () => {}, actions = [], action = ''}) => {
    const { contentSpecificParams, setContentSpecificParams} = useContentSpecificParams()
    const {
        assetRenderParametersMap,
        editorialAssets,
        loadEditorialAssets,
        selectedEditorialAssets,
        editorialAssetMutualSelection,
        addEditorialAssetParameterMap,
        addEditorialAssetParameterInterleaveMap,
        clearEditorialAssetParameterInterleaveMap,
        reorderEditorialAssetParameterInterleaveMap,
        renderEditorialInterleaveAsset,
        setSelectedEditorialAssets,
        setAssetRenderParametersMap,
        resetEditorialAssetSelection,
        getAssetDefaultParameters,
        isMaterializing,
        setIsMaterializing,
    } = useEditorialAssets();
    const {updateSelectionCards, contentType, setContentType, selectedClipCards, previousPage, logicClip, setLogicClip, setLogicClipName, logicClipName, panelMode, selectedCard, setSelectedCard, removeSelectedCard, toggleLockedState, clearSelectedCard, sortOptions, setSortOptions, getEditorKeyMoments, lockKeyMoments, lockedKeyMoments} = useSelectedCard()
    const {setForwardClip}= usePageSettings()
    const { setEditorKeyMoments } = useEditorKeyMoments()
    const { setMode, setContentType: setCollectionContentType } = useSelectedContent()
    const { setEditorContentType, setLocalKeyMoment } = useEditState()
    const navigate = useNavigate();
    const location = useLocation();
    const {openSSE, jobs} = useSSEProvider();
    const { libraryJump } = usePageJump()
    const { previousPage: globalPrevPage, currentPage: globalCurrentPage} = useNavigationHistory()
    const intl = useIntl();

    const [gridView, setGridView] = React.useState(false);
    const [selectionDuration, setSelectionDuration] = useState<number>(0)

    const [audioTrackInfo, setAudioTrackInfo] = useState<AudioTrackInfos>({severity: "success", message: "", title: ""})
    const [eventsAudioTracks, setEventsAudioTracks] = useState<Record<string, string[]>>({})
    const [isAudioTrackCompatible, setIsAudioTrackCompatible] = useState<boolean>(false)
    const [audioTracksLoading, setAudioTracksLoading] = useState<boolean>(true)

    const {fade, fadeDuration, lCut, aspectRatio, audioTrack, setFade, setFadeDuration, setLCut, setAudioTrack, setAspectRatio, setSavedLCutOffsets, savedLCutOffsets} = useMaterializationSettings()
    const {overWriteClip, saveClip, applyLCut} = useClipOperations()
    type QueueItem = { type: 'start' | 'end'; tag: string };
    const queue = useRef<QueueItem[]>([]);
    const popoverRef = useRef<ButtonWithPopoverRef>(null)

    const [keyMomentsLoading, setKeyMomentsLoading] = useState<boolean>(false)
    const [suggestLoading, setSuggestLoading] = useState<boolean>(false)

    const isGoalParadeContentType = () => {
        const supportedContent = SUPPORTED_CONTENTS[CURRENT_SPORT] as { GOAL_PARADE?: string };
        return supportedContent.GOAL_PARADE !== undefined && contentType === supportedContent.GOAL_PARADE;
    };

    const setSpecificParamsField = (field: string, value: any) => {
        setContentSpecificParams((prev: any) => {
            if(value === null || value?.id === null) return {...prev, [isGoalParadeContentType() ? 'matchday' : 'match']: null, [field]: value }
            if (!prev) return prev;
            if (field === 'competition' && usageMode !== AGGREGATION_USAGE_TYPE.PRESET) {
                loadEditorialAssets(contentType, value.id)
                if (prev.competition && prev.competition.id !== value.id){
                    return {...prev, [isGoalParadeContentType() ? 'matchday' : 'match']: null, [field]: value}
                }
                if (!prev.competition){
                    return {...prev, [field]: value}
                }
                return {...prev}
            }
            else if (field === 'match') {
                if(value){
                    if(value.home_team_code && value.away_team_code && value.start_schedule){
                        setLogicClipName(DEFAULT_CLIP_NAMES[CURRENT_SPORT][contentType as keyof typeof DEFAULT_CLIP_NAMES[typeof CURRENT_SPORT]](value.home_team_code, value.away_team_code, value.start_schedule ))

                    }else{
                        setLogicClipName(DEFAULT_CLIP_NAMES[CURRENT_SPORT][contentType as keyof typeof DEFAULT_CLIP_NAMES[typeof CURRENT_SPORT]](value.value, "", ""))
                    }
                }
                return {
                    ...prev,
                    [field]: value,
                };
            }
            else{
                return {
                    ...prev,
                    [field]: value,
                };
            }
        });
    };

    const handleChangeOrder = (ordered: CollectionClip[]) => {
        setSelectedCard(ordered);
    };

    const handleSort = (sortBy: string, sortOrder: 'asc' | 'desc') => {
        if (sortBy) {
            setSelectedCard(sortObjects(selectedCard, sortBy as any, sortOrder));
        }
    };

    const onMatchHighlightParamChange = (keyMomentName: string, value: boolean) => {
        setContentSpecificParams((prev: { keyMomentSettings: any; }) => {
            if (!prev) return prev;
            const tempKeyMomentSettings = [...prev.keyMomentSettings]
            const targetKeyMomentSetting = tempKeyMomentSettings.find(kms => kms.name === keyMomentName);
            if (targetKeyMomentSetting) {
                targetKeyMomentSetting.selected = value
                return {
                    ...prev,
                    keyMomentSettings: tempKeyMomentSettings,
                };
            }else{
                return prev
            }
        });
    }

    const onMatchHighlightOptionChange = (keyMomentName: string, optionName: string, value: boolean) => {
        setContentSpecificParams((prev: any) => {
            if (!prev || !prev.keyMomentSettings) return prev;

            // Copiamo l'array per mantenere l'immutabilità dello stato di React
            const tempKeyMomentSettings = [...prev.keyMomentSettings];

            // Troviamo il key moment corrispondente (es. "GOAL" o "CARD")
            const targetKeyMomentSetting = tempKeyMomentSettings.find((kms: any) => kms.name === keyMomentName);

            if (targetKeyMomentSetting) {
                if (targetKeyMomentSetting.options && !Array.isArray(targetKeyMomentSetting.options) && targetKeyMomentSetting.options.values) {
                    if (targetKeyMomentSetting.options.schema === HIGHLIGHT_OPTION_SHEMA.CHECKBOX) {
                        const targetOption = targetKeyMomentSetting.options.values.find((opt: any) => opt.name === optionName);
                        if (targetOption) {
                            targetOption.value = value;
                        }
                    }
                    // COMPORTAMENTO RADIOBUTTON: l'opzione target diventa true, tutte le altre diventano false
                    else if (targetKeyMomentSetting.options.schema === HIGHLIGHT_OPTION_SHEMA.RADIO_BUTTON) {
                        targetKeyMomentSetting.options.values.forEach((opt: any) => {
                            opt.value = (opt.name === optionName);
                        });
                    }
                }

                return {
                    ...prev,
                    keyMomentSettings: tempKeyMomentSettings,
                };
            } else {
                return prev;
            }
        });
    };

    const onSelectAllMatchHighlights = (all: boolean) => {
        setContentSpecificParams((prev: { keyMomentSettings: any[]; }) => {
            if (!prev) return prev;
            const tempKeyMomentSettings = prev.keyMomentSettings.map((kms: any) => ({...kms, selected: all}))
            return {
                ...prev,
                keyMomentSettings: tempKeyMomentSettings,
            };
        });
    }

    const presetInfo = async (onlyEditorialAsset: boolean = false) => {
        const logicClipWithKeyMomentsCards = await getLogicKeyMomentsAsCards(logicClip?.id)
        const keyMomentsCards = logicClipWithKeyMomentsCards.key_moments
        const creationSettings = logicClipWithKeyMomentsCards.creation_settings
        lockKeyMoments(keyMomentsCards, contentType)
        if(!onlyEditorialAsset) setSelectedCard(keyMomentsCards)

        const tempContentSpecificParams: any = {
            maxDuration: creationSettings.suggest_params ? creationSettings.suggest_params.max_duration || 0 : 0,
            minDuration: creationSettings.suggest_params ? creationSettings.suggest_params.min_duration || 0 : 0,
        }

        if(creationSettings && !onlyEditorialAsset){
            setFade(creationSettings.fade || false)
            setFadeDuration(creationSettings.fade_duration || defaultFadeDuration)
            setLCut(creationSettings.lcut || false)
            setSavedLCutOffsets(
                creationSettings.lcut_offset && typeof creationSettings.lcut_offset === 'object' && !Array.isArray(creationSettings.lcut_offset)
                    ? Object.fromEntries(
                        Object.entries(creationSettings.lcut_offset).map(([idClip, offset]) => [idClip, Math.max(0, offset as number)])
                    )
                    : {}
            )
            setAspectRatio(findClosestAspectRatio(creationSettings.aspect_ratio, ASPECT_RATIO_OPTIONS.map((ratio:any) => ratio.label)))
        }

        //Caso MATCH_HIGHLIGHT
        if(location.pathname.includes(SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].MATCH_HIGHLIGHT.toLowerCase()) && keyMomentsCards.length > 0){
            const event: FilterOption = {
                id: keyMomentsCards[0].event.id,
                value: `${keyMomentsCards[0].event.name}/${formatUTCDate(keyMomentsCards[0].event.start_schedule)}`
            }
            const competition: FilterOption = {
                id: keyMomentsCards[0].event.competition.id,
                value: `${keyMomentsCards[0].event.competition.name} - ${keyMomentsCards[0].event.competition.season}`
            }
            tempContentSpecificParams.match = event;
            tempContentSpecificParams.competition = competition;
            tempContentSpecificParams.keyMomentSettings = (defaultContentSpecificParams as any)[CURRENT_SPORT].MATCH_HIGHLIGHT.keyMomentSettings;
        }

        if(CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL && location.pathname.includes(SUPPORTED_CONTENTS[SUPPORTED_SPORTS.FOOTBALL].GOAL_PARADE.toLowerCase()) && keyMomentsCards.length > 0){
            const matchDay: FilterOption = {
                id: keyMomentsCards[0].event.full_matchday ? keyMomentsCards[0].event.full_matchday.id : null,
                value: keyMomentsCards[0].event.full_matchday ? `${keyMomentsCards[0].event.full_matchday.name} N°(${keyMomentsCards[0].event.full_matchday.number})` : ""
            }
            const competition: FilterOption = {
                id: keyMomentsCards[0].event.competition.id,
                value: `${keyMomentsCards[0].event.competition.name} - ${keyMomentsCards[0].event.competition.season}`
            }
            tempContentSpecificParams.matchday = matchDay;
            tempContentSpecificParams.competition = competition;
            tempContentSpecificParams.keyMomentSettings = (defaultContentSpecificParams as any)[CURRENT_SPORT].GOAL_PARADE.keyMomentSettings;
        }

        const competitionIdToFetch = tempContentSpecificParams.competition?.id || null;

        if (!onlyEditorialAsset && usageMode !== AGGREGATION_USAGE_TYPE.PRESET) {
            loadEditorialAssets(contentType, competitionIdToFetch).catch(e => {
                console.error(e);
                enqueueSnackbar("Failed to load specific editorial assets for this clip");
            });
        }

        if(!onlyEditorialAsset){
            setContentSpecificParams(tempContentSpecificParams)
            setAudioTrack({id: logicClipWithKeyMomentsCards.audio_type, value: logicClipWithKeyMomentsCards.audio_type})
            setLogicClipName(logicClipWithKeyMomentsCards.name)
        }


        const { id, fade, fade_duration, lcut, lcut_offset, content_type, aspect_ratio, pix_fmt, suggest_params, ...editorialAssetSettings } = logicClipWithKeyMomentsCards.creation_settings;

        const tempSelectedEditorialAssets: EditorialAsset[] = []
        const assetPromises: Promise<any>[] = [];

        const tempAssetRenderParmMap: Record<string, any>= {}
        if (editorialAssets.length > 0) {
            for (const type in editorialAssetSettings) {
                const editorialAssetSetting = editorialAssetSettings[type]
                if (editorialAssetSetting){
                    for (const [index, graphicOverlay] of editorialAssetSetting.entries()) {
                        const targetEditorialAsset: EditorialAsset = editorialAssets.find(e => e.id === graphicOverlay.id);
                        if (targetEditorialAsset) {
                            const isAlreadySelected = tempSelectedEditorialAssets.some(asset => asset.id === targetEditorialAsset.id);

                            if (!isAlreadySelected) {
                                tempSelectedEditorialAssets.push(targetEditorialAsset);
                            }
                            if(type !== EDITORIAL_ASSETS.INTERLEAVE){
                                tempAssetRenderParmMap[targetEditorialAsset.id] = {overlay_params: graphicOverlay.params.data}
                                if(targetEditorialAsset.parameters.render_parameters && targetEditorialAsset.parameters.render_parameters.length > 0){
                                    const promise = getAssetDefaultParameters(targetEditorialAsset.id, content_type).then(asyncResult => {
                                        return {
                                            id: targetEditorialAsset.id,
                                            infos: asyncResult.infos
                                        };
                                    });
                                    assetPromises.push(promise);
                                }
                            } else {
                                const newAssedInterleaveId = getOverlayUniqueId(targetEditorialAsset.id + '_ASSET_MID_' + index);
                                tempAssetRenderParmMap[targetEditorialAsset.id+'_&_'+newAssedInterleaveId] = {overlay_params: graphicOverlay.params.data}
                            }
                        }else{
                            console.error("Logic clip editorial asset does not exist", graphicOverlay);
                        }
                    }
                }
            }
        }

        setSelectedEditorialAssets(tempSelectedEditorialAssets)
        if(!onlyEditorialAsset){
            setContentSpecificParams(tempContentSpecificParams)
            setAudioTrack({id: logicClipWithKeyMomentsCards.audio_type, value: logicClipWithKeyMomentsCards.audio_type})
            setLogicClipName(logicClipWithKeyMomentsCards.name)
        }

        const resolvedAssets = await Promise.all(assetPromises);
        for (const item of resolvedAssets) {
            tempAssetRenderParmMap[item.id].infos = item.infos;
        }
        setAssetRenderParametersMap(tempAssetRenderParmMap)

    }

    const convertClipToKeyMoments = async () => {
        try{
            const keyMoments = await getClipsKeyMomentByIds(selectedClipCards.map(card => card.id), true)
            const {updatedCards, cutDuration, originalDuration} = getCardSelectionDuration(keyMoments, SELECTION_LIMIT_MINUTES)
            if (cutDuration !== originalDuration){
                enqueueSnackbar(`The key moments selection window was too long and has been reduced to ${SELECTION_LIMIT_MINUTES} minutes`)
            }
            setSelectedCard(updatedCards)
        }catch(e){
            console.error(e)
            enqueueSnackbar("Failed to split clips into their respective key moments")
        }
    }

    const loadEditorialAssetRenderParams = async (editorialAssetId: string) =>{
        try{
            await addEditorialAssetParameterMap(editorialAssetId, contentType)
        }catch (e){
            console.error(e);
            enqueueSnackbar("Failed to load editorial assets render parameters")
        }
    }

    const loadEditorialAssetRenderInterleaveParams = async (editorialAssetId: string, overlayId: string, localRenderParams: OverlayRenderParametersResponse) =>{
        try{
            await addEditorialAssetParameterInterleaveMap(editorialAssetId, overlayId, contentType, localRenderParams)
        }catch (e){
            console.error(e);
            enqueueSnackbar("Failed to load editorial assets render parameters")
        }
    }

    // Profiling: bracket the create-clip flow's end — fire once after the editor
    // has assets and has painted. No-op unless VITE_RENDER_PROFILING is enabled.
    const caPaintedMarkedRef = useRef(false);
    useEffect(() => {
        if(!caPaintedMarkedRef.current && editorialAssets.length > 0){
            caPaintedMarkedRef.current = true;
            profMarkAfterPaint("CA:painted");
        }
    }, [editorialAssets]);

    useEffect(() => {
        profMark("CA:mount")
        setCollectionContentType("key_moments")
        if (usageMode !== AGGREGATION_USAGE_TYPE.PRESET && logicClip && panelMode === PANEL_MODE.UPDATE && selectedCard.length === 0) {presetInfo()}
        let tempContentType: "MATCH_HIGHLIGHT" | "GOAL_PARADE" | "CLIP"  = SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].MATCH_HIGHLIGHT
        if (location.pathname.includes(SUPPORTED_CONTENTS[CURRENT_SPORT].MATCH_HIGHLIGHT.toLowerCase())) {
            tempContentType = SUPPORTED_CONTENTS[CURRENT_SPORT].MATCH_HIGHLIGHT
        }else if(CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL && location.pathname.includes(SUPPORTED_CONTENTS[CURRENT_SPORT].GOAL_PARADE.toLowerCase())){
            tempContentType = SUPPORTED_CONTENTS[CURRENT_SPORT].GOAL_PARADE
        }else{
            tempContentType = SUPPORTED_CONTENTS[CURRENT_SPORT].CLIP
        }
        if(usageMode !== AGGREGATION_USAGE_TYPE.PRESET) setContentType(tempContentType as any);
        if (usageMode !== AGGREGATION_USAGE_TYPE.PRESET) {
            if (panelMode === PANEL_MODE.CREATE && tempContentType === SUPPORTED_CONTENTS[CURRENT_SPORT].CLIP) {

                loadEditorialAssets(tempContentType, null).catch(console.error);
            }
        }
        setEditorContentType(EDITOR_CONTENT_TYPE.KEY_MOMENT)
        if (!contentSpecificParams && CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL && tempContentType === SUPPORTED_CONTENTS.FOOTBALL.MATCH_HIGHLIGHT){
            setContentSpecificParams(defaultContentSpecificParams[SUPPORTED_SPORTS.FOOTBALL][SUPPORTED_CONTENTS.FOOTBALL.MATCH_HIGHLIGHT]);
        }
        if (!contentSpecificParams && CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL && (tempContentType as string) === SUPPORTED_CONTENTS.FOOTBALL.GOAL_PARADE
        ) {
            setContentSpecificParams(
                defaultContentSpecificParams[SUPPORTED_SPORTS.FOOTBALL][SUPPORTED_CONTENTS.FOOTBALL.GOAL_PARADE]
            );
        }
        if (!contentSpecificParams && CURRENT_SPORT === SUPPORTED_SPORTS.VOLLEY && tempContentType === SUPPORTED_CONTENTS.VOLLEY.MATCH_HIGHLIGHT){
            setContentSpecificParams(defaultContentSpecificParams[SUPPORTED_SPORTS.VOLLEY][SUPPORTED_CONTENTS.VOLLEY.MATCH_HIGHLIGHT]);
        }

        if(previousPage === CLIP_PAGE_NAME){
            convertClipToKeyMoments()
        }
    }, [location])

    useEffect(() => {
        if(logicClip && panelMode === PANEL_MODE.UPDATE && editorialAssets.length > 0 && Object.keys(assetRenderParametersMap).length === 0){
            if(globalPrevPage && !globalPrevPage.includes(EDITOR_PAGE_NAME.toLowerCase()) && !globalCurrentPage.includes(EDITOR_PAGE_NAME.toLowerCase())){
                presetInfo(true)
            }
        }
    }, [editorialAssets]);

    /*const applyKeyMomentsLocalOptions = (keyMoments: CollectionClip[]) => {
        const filteredKeyMoments: CollectionClip[] = []
        for (const keyMoment of keyMoments){
            const specificParams = contentSpecificParams?.keyMomentSettings.find((ks: any) => ks.name === keyMoment.title)
            if ((keyMoment.tags || []).includes('RED_CARD')){
                if(specificParams.options.schema === HIGHLIGHT_OPTION_SHEMA.RADIO_BUTTON){
                    const radioButton = specificParams.options.values.find((option: any) => option.value === true);
                    if(!(radioButton && radioButton.name === 'RED_ONLY')){
                        filteredKeyMoments.push(keyMoment)
                    }
                }
            }else if (keyMoment.title === 'GOAL'){
                for(const cut of (keyMoment.key_moments || [])){
                    for(let trimPointIndex = 0; trimPointIndex < cut.cuts.length; trimPointIndex++){
                        if(specificParams.options.schema === HIGHLIGHT_OPTION_SHEMA.CHECKBOX){
                            const checkboxValues = specificParams.options.values.filter((option: any) => option.value === false);
                            const trimPoint = cut.cuts[trimPointIndex]
                            for(const checkboxValue of checkboxValues){
                                if(checkboxValue.name === 'VAR' && trimPoint.label === 'VAR'){
                                    cut.cuts.splice(trimPointIndex, 1)
                                }
                                else if(checkboxValue.name === 'REPLAY' && trimPoint.label === 'REPLAY'){
                                    cut.cuts.splice(trimPointIndex, 1)
                                }
                            }
                        }
                    }
                }
            }else{
                filteredKeyMoments.push(keyMoment)
            }
        }
        return filteredKeyMoments;
    }*/

    const handleSuggest = async () => {
        try{
            const filters : Record<string, {id: string, value: string}[]> = {keyMomentTypeValues: [], matchFilterValues: []}
            filters.keyMomentTypeValues = contentSpecificParams!.keyMomentSettings.filter((km: { selected: any; }) => km.selected).map((kms: { name: any; }) => ({id: kms.name, value: kms.name}))
            const sportConfig = SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS];
            if ('MATCH_HIGHLIGHT' in sportConfig && contentType === sportConfig.MATCH_HIGHLIGHT) {
                if (contentSpecificParams?.match) {
                    filters.matchFilterValues = [{ id: contentSpecificParams.match.id, value: contentSpecificParams.match.name }];
                }
            }else if(CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL && (contentType as string) === SUPPORTED_CONTENTS.FOOTBALL.GOAL_PARADE){
                filters.competitionFilterValues = [{id: contentSpecificParams!.competition!.id, value: contentSpecificParams!.competition!.name}]
                filters.matchDayFilterValues = [{id: contentSpecificParams!.matchday!.id, value: contentSpecificParams!.matchday!.name}]
            }
            setKeyMomentsLoading(true);
            const keyMoments = await getKeyMoments(filters, null, null, 'global_seconds', 'asc', applyKeyMomentFilter(contentSpecificParams!.keyMomentSettings), true)
            lockKeyMoments(keyMoments, contentType)
            const selectedKeyMoments = mapSelection(keyMoments)
            setSelectedCard(sortOptions.sortBy
                ? sortObjects(selectedKeyMoments, sortOptions.sortBy as any, sortOptions.sortOrder)
                : selectedKeyMoments
            )
            if (keyMoments.length === 0){
                enqueueSnackbar("No key moments match the selected search settings.")
            }
            setKeyMomentsLoading(false);
        }catch(e){
            setKeyMomentsLoading(false);
            console.error(e)
            enqueueSnackbar("Retrieve Key Moments Failed")
        }
    }

    const onProduceClick = async () => {
        try {
            enqueueSnackbar("Render Request Started");
            const reqLogicClip = logicClip && panelMode === PANEL_MODE.UPDATE ? await processClipData(overWriteClip) : await processClipData(saveClip);
            const materializationSettings = { fade, fadeDuration, lCut, openerId: [], closerId: [], aspectRatio };

            // Eseguiamo la chiamata
            const response: any = await materializeLogicClip(reqLogicClip.id, materializationSettings);

            // CASO 1: Risposta immediata (materializzazione completata senza SSE)
            if (response && response.new === false && response.materialization) {
                setLogicClip((prev: any) => {
                    if (!prev) return prev;
                    const updatedMaterializations = prev.materializations ? [...prev.materializations] : [];
                    updatedMaterializations[0] = response.materialization;
                    return { ...prev, materializations: updatedMaterializations };
                });
                enqueueSnackbar("Render completed immediately!");
            }
            // CASO 2: Job accodato, tracking affidato all'SSE
            else if (response && response.id) {
                enqueueSnackbar("Render Request Accepted. Agent Started.");
            }
        } catch(e) {
            console.error(e);
            enqueueSnackbar("Problems Producing the clip");
        }
    }

    useEffect(() => {

        const jobsCompleted = jobs.filter((job : any) => job.status.toLowerCase() === 'completed' && job.type === SSEJobs.PRODUCTION_ON_DEMAND)

        if (!logicClip || !jobsCompleted || jobsCompleted.length === 0) return;

        // Troviamo i job completati associati a questa clip e prendiamo il più recente
        const completedClipJobs = jobsCompleted
            .filter((job: any) => job.logic_clip && job.logic_clip.id === logicClip.id)
            .sort((a: any, b: any) => {
                const timeA = new Date(a.completedAt || 0).getTime();
                const timeB = new Date(b.completedAt || 0).getTime();
                return timeB - timeA; // Ordine decrescente
            });

        if (completedClipJobs.length > 0) {
            const latestCompletedJob = completedClipJobs[0];
            const newMat = latestCompletedJob.logic_clip.materializations?.[0];

            if (newMat) {
                setLogicClip((prev: any) => {
                    if (!prev) return prev;
                    const currentMatId = prev.materializations?.[0]?.id;

                    if (currentMatId !== newMat.id) {
                        const updatedMaterializations = prev.materializations ? [...prev.materializations] : [];
                        updatedMaterializations[0] = newMat;
                        enqueueSnackbar("Clip Rendered Successfully");
                        return { ...prev, materializations: updatedMaterializations };
                    }
                    return prev;
                });
            }
        }
    }, [jobs, logicClip]);

    const activeJob = useMemo(() => {

        const jobsRunning = jobs.filter((job : any) => job.status.toLowerCase() === 'running' && job.type === SSEJobs.PRODUCTION_ON_DEMAND)

        if (!logicClip || !jobsRunning || jobsRunning.length === 0) return null;

        // Filtriamo solo i job appartenenti a questa specifica logic_clip
        const currentClipJobs = jobsRunning.filter((job: any) => job.logic_clip && job.logic_clip.id === logicClip.id);

        if (currentClipJobs.length === 0) return null;

        // Se ci sono multipli job, prendiamo il più recente (basato su un timestamp, se disponibile)
        return currentClipJobs.sort((a: any, b: any) => {
            const timeA = new Date(a.created_at || a.updated_at || 0).getTime();
            const timeB = new Date(b.created_at || b.updated_at || 0).getTime();
            return timeB - timeA; // Ordine decrescente, l'indice 0 è il più recente
        })[0];
    }, [jobs, logicClip]);

    useEffect(() => {
        openSSE()
    }, []);

    const onStopRendering = async () =>{
        try {
            const response = await stopJob(activeJob.id);
            if(response.status === 200){
                enqueueSnackbar("Rendering Job successfully Stopped")
            }
        }catch(error) {
            enqueueSnackbar("Error Stopping Rendering Agent")
        }
    }

    const prevSortOptionsRef = useRef(sortOptions);
    const isFirstSortEffectRun = useRef(true);

    useEffect(() => {
        if (isFirstSortEffectRun.current) {
            isFirstSortEffectRun.current = false;
            prevSortOptionsRef.current = sortOptions;
            return;
        }
        let eventAsset = selectedEditorialAssets.find((a) => a.placement_type === EDITORIAL_ASSETS.INTERLEAVE && a.parameters.technical_parameters.mode === INTERLEAVE_MODE.EVENT);
        if(eventAsset) {
            const prevSortOptions = prevSortOptionsRef.current;
            const onlyOrderChanged = prevSortOptions.sortBy === sortOptions.sortBy && prevSortOptions.sortOrder !== sortOptions.sortOrder;

            if (!(onlyOrderChanged && reorderEditorialAssetParameterInterleaveMap(eventAsset.id))) {
                clearEditorialAssetParameterInterleaveMap(eventAsset.id)
                onAssetChange(eventAsset, false)
            }
        }
        prevSortOptionsRef.current = sortOptions;
    }, [sortOptions]);

    const processClipData = async (apiFunction: any) => {
        if (lCut) {
            const lCutCards: CollectionClip[] = applyLCut(selectedCard);
            const startingCuts: KeyMomentCut[] = extractPlatCuts(lCutCards);
            const newCuts = await createOrphanCutsCopy(startingCuts);
            const updatedCards = updateSelectionCards(newCuts);
            return await apiFunction(contentType, updatedCards);
        } else {
            return await apiFunction(contentType);
        }
    };

    const onSaveClip = async () => {
        try {
            popoverRef.current!.close();
            enqueueSnackbar("Save Request Started");
            await processClipData(saveClip);
            enqueueSnackbar("Clip Saved Successful");
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Problems Saving the clip");
        }
    };

    const onOverWriteClip = async () => {
        try {
            popoverRef.current!.close();
            enqueueSnackbar("Overwrite Request Started");
            await processClipData(overWriteClip);
            enqueueSnackbar("Clip Overwrite Successful");
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Problems Overwriting the clip");
        }
    };

    const onChoose = () => {
        libraryJump(contentType)
    }

    const onEdit = () => {
        const basePath = `${getSubRoot(window.location.pathname)}/clips/${EDITOR_PAGE_NAME}`
        setForwardClip({
            selectedClipsId: selectedCard.map(card => card.id),
            contentType: EDITOR_CONTENT_TYPE.KEY_MOMENT
        })
        setLocalKeyMoment(null)
        setMode(panelMode ===  PANEL_MODE.CREATE ? ModeStatus.CREATE : ModeStatus.EDIT)
        setEditorContentType(EDITOR_CONTENT_TYPE.CLIP)
        setEditorKeyMoments(getEditorKeyMoments())
        navigate(basePath + (logicClip? `/?id=${logicClip.id}` : ''))
    }

    const onFitInTimeClick = async () => {
        try{
            setSuggestLoading(true)
            const keyMoments = await suggest(
                contentSpecificParams?.match?.id,
                contentSpecificParams?.minDuration,
                contentSpecificParams?.maxDuration,
                contentType,
                null,
                contentSpecificParams?.matchday?.id,
                contentSpecificParams?.competition?.id,
                undefined,//contentSpecificParams?.keyMomentSettings.filter(km => km.selected).map(item => item.name)
                selectedCard.map(card => card.id),
                contentSpecificParams.keyMomentSettings ? applyKeyMomentFilter(contentSpecificParams.keyMomentSettings): undefined,
                true,
                Object.keys(lockedKeyMoments || {})
            )
            const selectedKeyMoments = mapSelection(keyMoments)
            setSelectedCard(sortOptions.sortBy
                ? sortObjects(selectedKeyMoments, sortOptions.sortBy as any, sortOptions.sortOrder)
                : selectedKeyMoments
            )
            setSuggestLoading(false)
        }catch (e){
            setSuggestLoading(false)
            console.error(e)
            enqueueSnackbar("Error suggesting key moments")
        }
    }

    useEffect(() => {

        /*let targetKeyMomentCards = [...selectedCard]

        if(lCut){
            const targetKeyMomentCards: CollectionClip[] = applyLCut(selectedCard)
            for(const card of targetKeyMomentCards){
                const masterCut = card.key_moments && card.key_moments[0]
                if(masterCut){
                    masterCut.cuts = masterCut.cuts.filter(tm => tm.label !== 'REPLAY')
                }
            }
        }*/

        let targetKeyMomentCards = selectedCard.map((km) => (
            {...km, cut: km.key_moments && km.key_moments[0] ? km.key_moments[0].plat_cut : {}}
        ))

        let allOverlays: ClipOverlay[] = [];
        let currentCursor = 0;

        for (const keyMoment of targetKeyMomentCards) {
            const { overlays, nextCursor } = mapKeyMomentToOverlays(keyMoment, 'clip', currentCursor, MAIN_TRACK_ID, intl, '', FPS);
            allOverlays.push(...overlays);
            currentCursor = nextCursor;
        }

        if(lCut){
            allOverlays = moveReplaySegmentsToReplayTrack(allOverlays, savedLCutOffsets)
        }

        for (const editorialAsset of selectedEditorialAssets) {
            allOverlays = addEditorialAsset(allOverlays, editorialAsset, selectedEditorialAssets, assetRenderParametersMap).changedOverlays
        }

        const maxEndFrame = Math.max(
            ...allOverlays.map(overlay => overlay.from + overlay.durationInFrames)
        );

        setSelectionDuration(maxEndFrame/FPS)


    }, [selectedCard, lCut, selectedEditorialAssets]);

    const deltaDuration = useMemo(() => {
        const maxDuration = contentSpecificParams?.maxDuration;
        const minDuration = contentSpecificParams?.minDuration;
        let delta = {
            rawDelta: '0',
            isGood: true
        }

        if (maxDuration !== undefined && minDuration !== undefined && selectionDuration){
            if (selectionDuration > maxDuration){
                 delta = {
                    rawDelta: `+ ${formatTime(selectionDuration - maxDuration)}`,
                    isGood: false
                };
            }
            else if (selectionDuration < minDuration){
                delta = {
                    rawDelta: `- ${formatTime(minDuration - selectionDuration)}`,
                    isGood: false
                };
            }
        }
        return delta
    }, [contentSpecificParams, selectionDuration]);

    const chooseDisabled = useMemo(() => {
        try {
            if(contentType === SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].MATCH_HIGHLIGHT){
                return (!contentSpecificParams?.match || !contentSpecificParams?.competition) && selectedCard.length === 0
            }
            if(contentType === SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].GOAL_PARADE){
                return (!contentSpecificParams?.matchday || !contentSpecificParams?.competition) && selectedCard.length === 0
            }
            return false
        } catch(e) {
            return false
        }
    }, [contentSpecificParams, contentType]);

    const editDisabledReason = useMemo(() => {
        if (selectedCard.length === 0) return "Select at least one key moment before editing";
        if (isMaterializing) return "Wait for the current rendering to finish";
        if (!audioTrack) return "Select an audio track";
        if (!isAudioTrackCompatible) return audioTrackInfo.message || "The selected audio track is not compatible with this selection";
        return "";
    }, [selectedCard.length, isMaterializing, audioTrack, isAudioTrackCompatible, audioTrackInfo.message]);

    const getTotalTrimPointsCount = (items: any[], mode: string): number => {
        switch(mode){
            case INTERLEAVE_MODE.EVENT: return getDistinctEvents(items).length
            default :
                return items.reduce((total: number, item: any) => {
                    return total + (item.key_moments?.length ?? 0);
                }, 0);
        }
    };

    const manageQueue = (typeActionQueue: 'start' | 'end', tagQueue: string) => {
        if (typeActionQueue === 'start') {
            queue.current.push({ type: typeActionQueue, tag: tagQueue });
            setIsMaterializing(true)
        } else {
            const index = queue.current.findIndex(item => item.tag === tagQueue && item.type === 'start' && typeActionQueue === 'end' );
            
            if (index !== -1) {
                queue.current.splice(index, 1);                
            }

            if (queue.current.length === 0) {
                setIsMaterializing(false)
            }
        }
    };

    const onAssetChange = async (editorialAsset: EditorialAsset, externalChange = true) => {
        if(editorialAsset.parameters.render_parameters && editorialAsset.parameters.render_parameters.length > 0 && (usageMode !== AGGREGATION_USAGE_TYPE.PRESET)){
            if(editorialAsset.placement_type !== EDITORIAL_ASSETS.INTERLEAVE) {
                const selectedInterleave = selectedEditorialAssets.find((a) => a.placement_type === EDITORIAL_ASSETS.INTERLEAVE);
                if (selectedInterleave && editorialAsset.placement_type === EDITORIAL_ASSETS.OPENING_COVER) {
                    const parameters = selectedInterleave.parameters.technical_parameters
                    if(parameters && editorialAsset.parameters.technical_parameters.margin_dx >= 0 && parameters.position === 'sx' && parameters.margin_sx >= 0) {
                        enqueueSnackbar('Cannot add opening cover: cover has right margin and the selected interleave has left margin.');
                        return
                    }
                }

                if (selectedInterleave && editorialAsset.placement_type === EDITORIAL_ASSETS.CLOSING_COVER) {
                    const parameters = selectedInterleave.parameters.technical_parameters
                    if(parameters && editorialAsset.parameters.technical_parameters.margin_sx >= 0 && parameters.position === 'dx' && parameters.margin_dx >= 0) {
                        enqueueSnackbar('Cannot add closing cover: cover has left margin and the selected interleave has right margin.');
                        return
                    }
                }
                try{
                    enqueueSnackbar(`Rendering process for ${editorialAsset.placement_type.replace('_', ' ')} has started.`, {
                        key: `rendering-started-${editorialAsset.placement_type}`,
                        preventDuplicate: true
                    });
                
                    manageQueue( 'start', editorialAsset.placement_type);
                    editorialAssetMutualSelection(editorialAsset)
                    await loadEditorialAssetRenderParams(editorialAsset.id);

                    enqueueSnackbar(
                            `Rendering completed: ${editorialAsset.placement_type.replace('_',' ')} asset`, {
                                key: `rendering-completed-${editorialAsset.placement_type}`,
                                preventDuplicate: true
                            }
                        );
                } catch (e) {
                    enqueueSnackbar("The asset production was not completed successfully: " + editorialAsset.placement_type.replace('_', ' '), {
                        key: `rendering-error-${editorialAsset.placement_type}`,
                        preventDuplicate: true
                    });
                }
                finally{
                    manageQueue( 'end', editorialAsset.placement_type);
                }
            }
            else {
                const { position, margin_sx, margin_dx, mode, offset} = editorialAsset.parameters.technical_parameters || {};
                const sorted = sortObjects(selectedCard, sortOptions.sortBy as any, sortOptions.sortOrder);
                const overlaysInterleave = getTotalTrimPointsCount(sorted, mode)
                const extraNeeded = offset === undefined && (position === 'sx' || position === 'dx') && (margin_sx !== undefined && margin_sx >= 0) && (margin_dx !== undefined && margin_dx >= 0);
                const iterationCount = overlaysInterleave - (extraNeeded ? 0 : 1);
                let removeOpening = false
                const selectedOpener = selectedEditorialAssets.find((a) => a.placement_type === EDITORIAL_ASSETS.OPENING_COVER);
                if (selectedOpener) {
                    if(selectedOpener && editorialAsset.parameters.technical_parameters.position === 'sx' && editorialAsset.parameters.technical_parameters.margin_sx >= 0) {
                        if(editorialAsset.parameters.technical_parameters.mode === INTERLEAVE_MODE.EVENT){
                            removeOpening = true
                        }
                        else {
                            enqueueSnackbar('Cannot add interleave: opening cover has right margin and the selected interleave has left margin.');
                            return
                        }
                    }
                }
                const selectedCloser = selectedEditorialAssets.find((a) => a.placement_type === EDITORIAL_ASSETS.CLOSING_COVER);
                if (selectedCloser) {
                    if(selectedCloser && editorialAsset.parameters.technical_parameters.position === 'dx' && editorialAsset.parameters.technical_parameters.margin_dx >= 0) {
                        enqueueSnackbar('Cannot add interleave: closing cover has left margin and the selected interleave has right margin.');
                        return
                    }
                }

                manageQueue( 'start', editorialAsset.placement_type );
                if(externalChange)editorialAssetMutualSelection(editorialAsset, removeOpening)
                try {
                    enqueueSnackbar(`Rendering process for ${editorialAsset.placement_type.replace('_', ' ')} has started.`, {
                        key: `rendering-started-${editorialAsset.placement_type}`,
                        preventDuplicate: true
                    });
                    
                    let renderParams = {} as OverlayRenderParametersResponse;
                    renderParams = await getAssetDefaultRenderParameters(editorialAsset.id, getContentRenderParams(contentType, contentSpecificParams));
                    let distinctEvents = getDistinctEvents(sorted) || []

                    const iterationPromises = Array.from({ length: iterationCount }).map(async (_, i) => {
                        const assetOverlayId = String(getOverlayUniqueId(editorialAsset.id + '_ASSET_MID_' + i));
                        let currentValue: any = {};
                        if(editorialAsset.parameters && editorialAsset.parameters.technical_parameters){
                            switch(editorialAsset.parameters.technical_parameters.mode){
                                case INTERLEAVE_MODE.EVENT: {
                                    if(distinctEvents && distinctEvents.length>i) {
                                        let currentEvent: any = distinctEvents[i]
                                        let eventName = currentEvent ? currentEvent.home_team + ' - ' + currentEvent.away_team : ''
                                        currentValue = { "label": eventName };
                                    }
                                    break;
                                }
                                default: {
                                    let currentPos = i + 1;
                                    if(editorialAsset.order && editorialAsset.order === 'desc') {
                                        currentPos = iterationCount - i;
                                    }
                                    currentValue = { "number": currentPos }
                                }
                            }
                        }
                        let localRenderParams = {
                            ...renderParams,
                            placementType: OverlayType.INTERLEAVE,
                            overlay_params: currentValue 
                        };

                        await renderEditorialInterleaveAsset(editorialAsset.id, contentType, localRenderParams.overlay_params);
                        await loadEditorialAssetRenderInterleaveParams(editorialAsset.id, assetOverlayId, localRenderParams);
                    });

                    await Promise.all(iterationPromises);

                    enqueueSnackbar(
                        `Rendering completed: ${editorialAsset.placement_type.replace('_',' ')} asset`, {
                            key: `rendering-completed-${editorialAsset.placement_type}`,
                            preventDuplicate: true
                        }
                    );
                } catch (e) {
                    enqueueSnackbar("The asset production was not completed successfully: " + editorialAsset.placement_type.replace('_', ' '), {
                        key: `rendering-error-${editorialAsset.placement_type}`,
                        preventDuplicate: true
                    });
                }
                finally {
                    manageQueue( 'end', editorialAsset.placement_type);
                }
            }
        }
        else editorialAssetMutualSelection(editorialAsset)
    }

    const onPublish = () => {
        const basePath = getSubRoot(window.location.pathname)
        navigate(`${basePath}/publish`)
    }

    const isGetKeyMomentDisabled = (): boolean => {
        try{
            switch (contentType) {

            case SUPPORTED_CONTENTS[CURRENT_SPORT].MATCH_HIGHLIGHT:
                return Boolean(
                    !contentSpecificParams?.match ||
                    contentSpecificParams?.keyMomentSettings?.every((kms: { selected: any }) => !kms.selected)
                );

            case SUPPORTED_CONTENTS[CURRENT_SPORT].GOAL_PARADE:
                return Boolean(
                    !contentSpecificParams?.matchday ||
                    contentSpecificParams?.keyMomentSettings?.every((kms: { selected: any }) => !kms.selected)
                );

            default:
                return false;
            }
        } catch(e) { return false }
    };

    const onAssetRemove = (editorialAsset: EditorialAsset) => {
        editorialAssetMutualSelection(editorialAsset)
    }

    const selectedCardIdsStr = selectedCard.map(card => card.event.id).join(',');

    const audioTrackEventIds = useMemo(() => {
        switch (contentType) {
            case SUPPORTED_CONTENTS[CURRENT_SPORT].MATCH_HIGHLIGHT:
                return contentSpecificParams?.match?.id ? [contentSpecificParams.match.id] : [];

            case SUPPORTED_CONTENTS[CURRENT_SPORT].CLIP:
            case SUPPORTED_CONTENTS[CURRENT_SPORT].GOAL_PARADE:
                return selectedCardIdsStr ? selectedCardIdsStr.split(',') : [];

            default:
                return [];
        }
    }, [
        contentType,
        contentSpecificParams?.match?.id,
        selectedCardIdsStr
    ]);

    const onAudioOptionsLoad = (severity: "success" | "warning" | "error", additionalInfos: any) => {

        if (severity === 'error'){
            setAudioTrackInfo({
                severity,
                message:"Error: No audio track is available for the selected key moments. Clip rendering, saving and editing are unavailable",
                title: "Missing Audio Tracks"
            })
        }
        setEventsAudioTracks(additionalInfos.availableTracksPerEvent || {})
    }

    useEffect(() => {
        if(!audioTracksLoading){
            const isCompatible = audioTrack && selectedCard.every(card =>
                eventsAudioTracks[card.event.id]?.includes(audioTrack.id)
            );
            setIsAudioTrackCompatible(isCompatible)
            if(!isCompatible){
                setAudioTrackInfo({
                    severity:"error",
                    message: "Error: The selected audio track is not available for one or more key moments. Clip rendering, saving and editing are unavailable",
                    title: "Audio Tracks Incompatibility"
                })
            }else{
                setAudioTrackInfo({severity: "success", message:"", title: ""})
            }
        }

    }, [audioTrack, selectedCard, eventsAudioTracks]);

    useEffect(() => {
        if (selectedCard.length > 0 && !logicClipName){            
            try{
                switch (contentType){
                    case SUPPORTED_CONTENTS[CURRENT_SPORT].MATCH_HIGHLIGHT: {
                        switch(CURRENT_SPORT) {
                            case SUPPORTED_SPORTS.FOOTBALL:
                                if(contentSpecificParams?.match?.home_team_code && contentSpecificParams?.match?.away_team_code && contentSpecificParams?.match?.start_schedule){
                                    setLogicClipName(DEFAULT_CLIP_NAMES[CURRENT_SPORT][contentType as keyof typeof DEFAULT_CLIP_NAMES[typeof CURRENT_SPORT]](contentSpecificParams?.match?.home_team_code, contentSpecificParams?.match?.away_team_code, contentSpecificParams?.match?.start_schedule));
                                }else{
                                    setLogicClipName(DEFAULT_CLIP_NAMES[CURRENT_SPORT][contentType as keyof typeof DEFAULT_CLIP_NAMES[typeof CURRENT_SPORT]](contentSpecificParams?.match?.value, "", ""))
                                }
                                break;
                            default:
                                setLogicClipName(DEFAULT_CLIP_NAMES[CURRENT_SPORT][contentType as keyof typeof DEFAULT_CLIP_NAMES[typeof CURRENT_SPORT]](contentSpecificParams?.match?.value, '', ''))
                                break;
                        }
                    }
                    case SUPPORTED_CONTENTS[CURRENT_SPORT].GOAL_PARADE: {
                        setLogicClipName(DEFAULT_CLIP_NAMES[CURRENT_SPORT][contentType as keyof typeof DEFAULT_CLIP_NAMES[typeof CURRENT_SPORT]](contentSpecificParams?.matchday.value, contentSpecificParams?.competition?.value, ''))
                        break;
                    }
                }
            } catch(e) {}
        }
    }, [selectedCard, contentSpecificParams]);

    return(
    <Box>
        <Card sx={{px:2, py:1, borderRadius:'0px !important'}}>
            <Stack direction="column" spacing={2}>
                {usageMode !== AGGREGATION_USAGE_TYPE.PRESET && contentType !== SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].CLIP && !deltaDuration.isGood &&
                    <WarningBanner message={"Your Selection is not between the min and max duration chosen"} title={"Creation Warning"}/>
                }
                {usageMode !== AGGREGATION_USAGE_TYPE.PRESET && audioTrack && !isAudioTrackCompatible && audioTrackInfo.severity !== "success" &&
                    <WarningBanner severity={audioTrackInfo.severity} message={audioTrackInfo.message} title={audioTrackInfo.title}/>
                }
                <Box>
                    {contentSpecificParams && contentType === SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].MATCH_HIGHLIGHT &&
                        <Box>
                            <DividerLabel label={"Auto Selection"}/>
                            <MatchHighlightHeader
                                disabled = {selectedCard.length > 0}
                                presetMode =  {usageMode === AGGREGATION_USAGE_TYPE.PRESET}
                                match={(contentSpecificParams as MatchHighlightParams).match}
                                setFootballMatch={(match: FilterOption | null) => setSpecificParamsField('match', match)}
                                durationMin={(contentSpecificParams as MatchHighlightParams).minDuration}
                                durationMax={(contentSpecificParams as MatchHighlightParams).maxDuration}
                                onDurationChange={(type: "min" | "max", value: number) => setSpecificParamsField(type === 'min' ? 'minDuration' : 'maxDuration', value)}
                                competition={(contentSpecificParams as MatchHighlightParams).competition}
                                onCompetitionChange={(competition: FilterOption | null) => setSpecificParamsField('competition', competition)}
                            />
                        </Box>
                    }
                    {contentSpecificParams && CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL && (contentType as string) === SUPPORTED_CONTENTS[SUPPORTED_SPORTS.FOOTBALL].GOAL_PARADE &&
                        <Box>
                            <DividerLabel label={"Auto Selection"}/>
                            <GoalParadeHeader
                                disabled = {selectedCard.length > 0}
                                presetMode =  {usageMode === AGGREGATION_USAGE_TYPE.PRESET}
                                matchDay={(contentSpecificParams as GoalParadeParams).matchday}
                                setMatchDay={(match: FilterOption | null) => setSpecificParamsField('matchday', match)}
                                durationMin={(contentSpecificParams as GoalParadeParams).minDuration}
                                durationMax={(contentSpecificParams as GoalParadeParams).maxDuration}
                                onDurationChange={(type: "min" | "max", value: number) => setSpecificParamsField(type === 'min' ? 'minDuration' : 'maxDuration', value)}
                                competition={(contentSpecificParams as GoalParadeParams).competition}
                                onCompetitionChange={(competition: FilterOption | null) => setSpecificParamsField('competition', competition)}
                            />
                        </Box>
                    }
                    {contentSpecificParams && selectedCard.length === 0 && contentType !== SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].CLIP &&
                        <KeyMomentSettings
                            keyMomentOptions={(contentSpecificParams as MatchHighlightParams).keyMomentSettings}
                            col={2}
                            onChange={onMatchHighlightParamChange}
                            onSelectAll={onSelectAllMatchHighlights}
                            onSuggest={handleSuggest}
                            disableSuggest={isGetKeyMomentDisabled()}
                            presetMode={usageMode === AGGREGATION_USAGE_TYPE.PRESET}
                            onOptionChange={onMatchHighlightOptionChange}
                            loading={keyMomentsLoading}
                        />
                    }
                </Box>
                <Box>
                    {selectedCard.length > 0 &&
                        <Box display="flex" alignItems="center" alignContent="center" gap={2}>
                            {contentType !== SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].CLIP &&
                                <LoadingButton
                                    loading={suggestLoading}
                                    loadingPosition={"start"}
                                    onClick={onFitInTimeClick}
                                    startIcon={<AccessTimeIcon/>}
                                    variant="contained"
                                    disabled={deltaDuration.isGood}
                                    sx={{height: PANEL_BUTTON_HEIGHT, width: 175}}>
                                    Fit To Time Limits
                                </LoadingButton>
                            }

                            <LabelValueBox label={'Selection Duration'} value={formatTime(selectionDuration)} />

                            {contentType !== SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].CLIP &&
                                <>
                                    <LabelValueBox
                                        label={'Delta Duration'}
                                        value={deltaDuration.rawDelta}
                                        good={deltaDuration.isGood}
                                    />
                                    <Box sx={{ borderLeft: DASHED_SEPARATOR_STYLE, height: '40px' }} />
                                </>
                            }

                            <CustomSortingSelector contentType={contentType} onSort={handleSort} />

                            <Box sx={{ml: 'auto', display:"flex"}}>
                                <Button
                                    onClick={() => {
                                        clearSelectedCard();
                                        resetEditorialAssetSelection();
                                    }}
                                    startIcon={<DeleteForeverIcon/>}
                                    data-tour={"content_aggregation_clear_button"}
                                    variant="contained"
                                >
                                    Clear
                                </Button>

                                {selectedCard.length >= CARD_NUMBER_TO_ENABLE_GRID &&
                                    <>
                                        <Box sx={{ marginLeft: '10px', borderLeft: '1px dashed', borderColor: 'white', height: '40px' }} />
                                        <ViewToggle gridView={gridView} setGridView={setGridView} />
                                    </>
                                }
                            </Box>

                        </Box>
                    }
                    <SimpleComposer
                        keyMoments={selectedCard}
                        onChangeOrder={handleChangeOrder}
                        onCardRemove={removeSelectedCard}
                        onCardLockChange={toggleLockedState}
                        gridView={gridView}
                        audioTrack={audioTrack}
                        eventsAudioTracks={eventsAudioTracks}
                    />
                </Box>

                <DividerLabel label={"Production Settings"}/>
                <Grid container spacing={2} data-tour={"content_aggregation_production_settings_grid_container"}>
                    <Grid size={{ xs: 12, md: 9 }}>
                        <EditorialAssetList
                            editorialAssets={editorialAssets}
                            selectedEditorialAssets={selectedEditorialAssets}
                            onAssetSelection={onAssetChange}
                            onAssetDeselect={onAssetRemove}
                            layout={'horizontal'}
                            aspectRatio={aspectRatio}
                            usageMode={usageMode}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                        <MaterializationSettings
                            usageMode={usageMode}
                            eventIds={audioTrackEventIds}
                            onAudioTrackOptionLoad={onAudioOptionsLoad}
                            audioTrackAutoSelection={panelMode === PANEL_MODE.CREATE}
                            showAudioTrackSelection={true}
                            disableLCut={selectedCard.length === 0}
                            onAudioTrackLoading={setAudioTracksLoading}
                        />
                    </Grid>
                </Grid>

                <DividerLabel/>


                <Box width={'100%'} display={'flex'} gap={2} justifyContent={'flex-end'} alignItems={'center'}>
                    {usageMode !== AGGREGATION_USAGE_TYPE.PRESET && (
                        <>
                            {activeJob ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 3,
                                        width: '100%', // Forza il contenitore a occupare tutto lo spazio dei bottoni
                                        height: PANEL_BUTTON_HEIGHT
                                    }}
                                >
                                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={activeJob.progress || 0}
                                            sx={{
                                                flex: 1,
                                                height: 10,
                                                borderRadius: 5,
                                                // Migliorata la visibilità:
                                                backgroundColor: "rgba(255,255,255,0.1)",
                                                "& .MuiLinearProgress-bar": {
                                                    borderRadius: 5,
                                                }
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '45px' }}>
                                            {`${Math.round(activeJob.progress || 0)}%`}
                                        </Typography>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        startIcon={<StopIcon />}
                                        sx={OP_BUTTON_STYLE}
                                        onClick={onStopRendering}
                                    >
                                        Stop
                                    </Button>
                                </Box>
                            ) : (
                                /* --- SE NON C'È UN JOB: MOSTRA LA LISTA STANDARD DI BOTTONI --- */
                                <>
                                    <Button onClick={onChoose} data-tour={"content_aggregation_choose_button"} startIcon={<AutoStoriesIcon/>} variant="contained" sx={OP_BUTTON_STYLE} disabled={chooseDisabled}>Choose</Button>
                                    <Tooltip title={editDisabledReason} disableHoverListener={!editDisabledReason}>
                                        <span>
                                            <Button
                                                disabled={selectedCard.length === 0 || isMaterializing || !audioTrack || !isAudioTrackCompatible}
                                                onClick={onEdit}
                                                startIcon={<EditIcon/>}
                                                variant="contained"
                                                sx={OP_BUTTON_STYLE}
                                                data-tour={"content_aggregation_edit_button"}
                                            >
                                                Edit
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    <ButtonWithPopover
                                        startIcon={<SaveIcon/>}
                                        variant="contained"
                                        sx={OP_BUTTON_STYLE}
                                        ref={popoverRef}
                                        disabled={selectedCard.length === 0 || isMaterializing || !isAudioTrackCompatible}
                                        popoverContent = {
                                            <ConfirmTextField
                                                label="Clip Name"
                                                value={logicClipName}
                                                onChange={setLogicClipName}
                                                onConfirm={panelMode === PANEL_MODE.UPDATE && logicClip ? onOverWriteClip : onSaveClip}
                                                buttonSx={OP_BUTTON_STYLE}
                                                disableTextField={logicClipName && panelMode === PANEL_MODE.UPDATE && logicClip}
                                                disableButton={!logicClipName}
                                            />
                                        }
                                        data-tour={"content-aggregation-save-button"}
                                    >
                                        Save
                                    </ButtonWithPopover>

                                    <ButtonWithPopover
                                        startIcon={<FileCopyIcon/>}
                                        variant="contained"
                                        sx={OP_BUTTON_STYLE}
                                        ref={popoverRef}
                                        data-tour={"content_aggregation_copy_creation_button"}
                                        disabled={selectedCard.length === 0 || !(logicClip && panelMode === PANEL_MODE.UPDATE) || isMaterializing || !isAudioTrackCompatible}
                                        popoverContent = {
                                            <ConfirmTextField
                                                label="Clip Name"
                                                value={logicClipName}
                                                onChange={setLogicClipName}
                                                onConfirm={onSaveClip}
                                                buttonSx={OP_BUTTON_STYLE}
                                                disableButton={!logicClipName}
                                            />
                                        }
                                    >
                                        Create Copy
                                    </ButtonWithPopover>

                                    <ButtonWithPopover
                                        ref={popoverRef}
                                        disabled={selectedCard.length === 0 || isMaterializing || !audioTrack || !isAudioTrackCompatible}
                                        startIcon={<SlowMotionVideoIcon/>}
                                        variant="contained"
                                        sx={OP_BUTTON_STYLE}
                                        data-tour={"content_aggregation_render_button"}
                                        popoverContent = {
                                            <ConfirmTextField
                                                label="Clip Name"
                                                value={logicClipName}
                                                onChange={setLogicClipName}
                                                onConfirm={onProduceClick}
                                                buttonSx={OP_BUTTON_STYLE}
                                                disableButton={!logicClipName}
                                            />
                                        }
                                    >
                                        Render
                                    </ButtonWithPopover>

                                    <Button
                                        onClick={onPublish}
                                        startIcon={<ShareOutlinedIcon/>}
                                        data-tour={"content_aggregation_publish_icon"}
                                        variant="contained"
                                        sx={OP_BUTTON_STYLE}
                                        disabled={(!logicClip) || (!logicClip.materializations) || (logicClip.materializations.length < 1) || isMaterializing}
                                    >
                                        Publish
                                    </Button>
                                </>
                            )}
                        </>
                    )}

                    {/* Sezione Preset Mode (se necessaria) */}
                    {usageMode === AGGREGATION_USAGE_TYPE.PRESET && (
                        <>
                        <section data-tour={"rules_content_aggregation_actions_section"} className="overflow-visible">
                            <OptionButtonGroup
                                value={action}
                                options={actions.map(a => ({value: a, label: getActionLabel(a)}))}
                                labelField={"label"}
                                valueField={'value'}
                                onChange={(a:any) => onActionChange(a)}
                            />
                        </section>
                        </>
                    )}
                </Box>
            </Stack>
        </Card>
    </Box>
    )
}

export default ContentAggregation